// React
import { forwardRef, HTMLProps } from "react"

// Icons
import { FiSearch, BsCurrencyEuro } from "@/lib/icons"

// UI
import { classNames } from "@/lib/ui"
import { AnimatePresence, motion } from "@/lib/animations"

// Types of inputs
import { TextInput } from "@/components/form-controls/Input/TextInput"
import { PhoneInput } from "@/components/form-controls/Input/PhoneInput"
import { IBANInput } from "@/components/form-controls/Input/IBANInput"

// need to omit ref since we are using forward ref
// but i forgot exactly why
export type InputProps = Omit<HTMLProps<HTMLInputElement>, "ref"> & {
	hasError?: boolean
	classNameWrapper?: string
	showIcon?: "bs-currency-euro"
	"data-testid"?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{ className = "", classNameWrapper = "", hasError, showIcon, ...props },
		ref,
	) => {
		return (
			<div className={classNames("relative", classNameWrapper)}>
				{showIcon === "bs-currency-euro" && (
					<span
						className={classNames(
							"pointer-events-none absolute inset-y-0 left-0 flex h-full items-center pl-3",
						)}
						aria-hidden="true"
					>
						<BsCurrencyEuro />
					</span>
				)}

				{props.type === "phone" ? (
					<PhoneInput
						onChange={props.onChange}
						onBlur={props.onBlur} // @ts-expect-error onBlur type mismatch with PhoneInput
						value={props.value} // @ts-expect-error value type mismatch with PhoneInput
						hasError={props.hasError}
						id={String(props.name)}
						name={String(props.name)}
						{...props}
					/>
				) : props.type === "iban" ? (
					(() => {
						const { onChange, value } = props
						return (
							<IBANInput
								onChange={(ibanValue) => {
									if (typeof onChange === "function") {
										onChange({
											target: {
												value: ibanValue,
												name: props.name,
											},
										} as React.ChangeEvent<HTMLInputElement>)
									}
								}}
								onBlur={props.onBlur}
								value={
									typeof value === "string"
										? value
										: undefined
								}
								hasError={hasError}
								id={String(props.name)}
								name={String(props.name)}
								className={classNames(
									className,
									"rounded-md",
									"block w-full",
								)}
								disabled={props.disabled}
								placeholder={props.placeholder}
								readOnly={props.readOnly}
								required={props.required}
								data-testid={props["data-testid"]}
							/>
						)
					})()
				) : (
					<TextInput
						type={props.type}
						ref={ref}
						{...props}
						className={classNames(
							className,
							"appearance-none",
							showIcon === "bs-currency-euro" && "pl-8", // Padding left Because of the euro sign
							"border",
							hasError ? `border-red-300` : `border-gray-300`,
							hasError && `text-red-600`,
							"rounded-md shadow-sm",
							"placeholder-gray-400",
							"focus-within:text-dark focus:outline-none",
							hasError
								? `focus:ring-red-500`
								: `focus:ring-primary-500`,
							hasError
								? `focus:border-red-500`
								: `focus:border-primary-500`,
							"text-sm disabled:bg-gray-100",
						)}
					/>
				)}

				{/* Errors */}
				<AnimatePresence>
					{hasError && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{
								opacity: 1,
							}}
							exit={{ opacity: 0 }}
							key="error-icon"
							className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
						>
							<svg
								className="size-5 text-red-500"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fillRule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		)
	},
)
Input.displayName = "Input"

export function InputMask({
	className,
	...rest
}: HTMLProps<HTMLParagraphElement>) {
	return (
		<p
			data-testid="inputmask"
			className={classNames(
				className,
				"appearance-none rounded-md border border-transparent px-3 py-2 hover:border-gray-300 hover:bg-white disabled:bg-gray-100 sm:text-sm",
			)}
			{...rest}
		/>
	)
}

interface SearchInputProps extends HTMLProps<HTMLInputElement> {
	label?: string
	containerClassName?: string
	labelClassName?: string
}

export function SearchInput({
	label,
	name = "search",
	containerClassName,
	labelClassName,
	className,
	...rest
}: SearchInputProps) {
	return (
		<div
			className={classNames(
				containerClassName,
				"relative text-gray-500 focus-within:text-dark hover:text-gray-900 hover:placeholder-gray-900",
			)}
		>
			<label
				htmlFor={name}
				className={classNames(
					labelClassName,
					"pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3",
				)}
			>
				{label && <span className="sr-only">{label}</span>}
				<FiSearch />
			</label>
			<input
				id={name}
				name={name}
				className={classNames(
					className,
					"border border-transparent bg-gray-300 py-2 pl-10 pr-3 leading-5",
					"bg-opacity-25 focus:bg-white",
					"appearance-none rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500",
					"placeholder-gray-500 hover:placeholder-gray-900 focus:border-primary-500",
				)}
				type="search"
				placeholder={label}
				data-testid="searchinput"
				{...rest}
			/>
		</div>
	)
}
